import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { exportBrochureAsPdf, smartDownloadBlob, isIOSDevice } from "@/lib/staticExport";
import {
  House, ArrowLeft, MapPin, Buildings, DownloadSimple, WhatsappLogo,
  Phone, EnvelopeSimple, IdentificationCard, CheckCircle, Timer,
} from "@phosphor-icons/react";

/**
 * StimaComparativaBuilder · pagina che l'agente apre da
 *   /dashboard/stima-comparativa?lead=<lead_id>
 *
 * Prepara un PDF "Stima Comparativa" 4 pagine A4:
 *   01 · Copertina personalizzata (cliente, range, agenzia, logo)
 *   02 · Mercato OMI + trend 8 trimestri (indice ISTAT) + tempo medio vendita
 *   03 · Immobili comparabili dell'agenzia (priorità VENDUTI, poi attivi)
 *   04 · Conclusione + contatti + footer legale
 *
 * Tutti i dati provengono da fonti legali (zero scraping):
 *   - OMI Agenzia delle Entrate (geopoi.it)
 *   - Indice prezzi ISTAT/Banca d'Italia
 *   - Properties dell'agente nel suo CRM (proprie, non scrapate)
 */
export default function StimaComparativaBuilder() {
  const [params] = useSearchParams();
  const leadId = params.get("lead");
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [conclusion, setConclusion] = useState("");
  const [recommendedPrice, setRecommendedPrice] = useState("");
  const previewRef = useRef(null);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    api.get(`/stima-comparativa/${leadId}`)
      .then((r) => setData(r.data))
      .catch((err) => {
        const msg = err?.response?.data?.detail || "Errore caricamento dati";
        toast.error(msg);
      })
      .finally(() => setLoading(false));
  }, [leadId]);

  const onDownload = async () => {
    if (!previewRef.current) return;
    setExporting(true);
    try {
      const nodes = Array.from(
        previewRef.current.querySelectorAll('[data-pdf-page]'),
      );
      if (nodes.length < 4) {
        toast.error("Anteprima non pronta. Riprova tra un istante.");
        return;
      }
      const safeName = (data?.lead?.name || "cliente")
        .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
      const filename = `stima-comparativa-${safeName}.pdf`;
      const pdfBlob = await exportBrochureAsPdf({
        pageNodes: nodes.slice(0, 4),
        filename,
        autoSave: false,
      });
      await smartDownloadBlob(pdfBlob, filename, "application/pdf");
      if (isIOSDevice()) {
        toast.success("PDF pronto. Tocca condividi per inviarlo al cliente.");
      } else {
        toast.success("Stima Comparativa scaricata.");
      }
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Export fallito. Riprova.");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF7]">
        <div className="w-10 h-10 rounded-full border-3 border-[#7C8B6E] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF7] p-6">
        <div className="bg-white border border-[#EAE7DF] rounded-2xl p-8 max-w-md text-center">
          <House size={32} weight="duotone" className="text-[#C8C5BD] mx-auto" />
          <h2 className="font-editorial text-[20px] mt-3 text-[#1C1C1A]">Richiesta non trovata</h2>
          <button onClick={() => navigate("/dashboard/richieste-stima")} className="mt-4 text-[13px] underline">
            Torna alle richieste
          </button>
        </div>
      </div>
    );
  }

  const { lead, input, result, city, omi_snapshot, trend_8q,
          time_to_sell, comparables, agent } = data;
  const surface = input?.surface_sqm || 0;
  const trendMax = Math.max(...(trend_8q || []).map((t) => t.value), 1);
  const trendMin = Math.min(...(trend_8q || []).map((t) => t.value), trendMax);
  const brand = agent?.brand_color || "#7C8B6E";

  const waText = encodeURIComponent(
    `Buongiorno ${lead?.name?.split(" ")[0] || ""}, ho preparato la stima comparativa dettagliata della tua casa${city ? ` a ${city}` : ""}. La trovi in allegato. Quando ci sentiamo?`,
  );
  const cleanPhone = (lead?.phone || "").replace(/[^+\d]/g, "");

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#1C1C1A]" data-testid="stima-comparativa-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <button
          onClick={() => navigate("/dashboard/richieste-stima")}
          className="inline-flex items-center gap-1.5 text-[12.5px] text-[#6B6A65] hover:text-[#1C1C1A] mb-4"
          data-testid="back-to-leads"
        >
          <ArrowLeft size={14} weight="bold" /> Richieste
        </button>

        <div className="flex items-baseline justify-between gap-3 flex-wrap mb-4">
          <div>
            <h1 className="font-editorial text-[26px] sm:text-[32px] leading-tight">
              Stima Comparativa per {lead?.name || "—"}
            </h1>
            <p className="mt-1 text-[13px] text-[#6B6A65]">
              {city || "—"} · {surface} m² · stato {prettyState(input?.state)}
              {comparables?.some((c) => c.kind === "sold") && (
                <span className="ml-2 inline-flex items-center gap-1 text-[#5A6A4D]">
                  <CheckCircle size={12} weight="fill" /> include immobili venduti dall'agenzia
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onDownload}
              disabled={exporting}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#1C1C1A] hover:bg-black text-white text-[13px] font-semibold transition-colors disabled:opacity-60"
              data-testid="export-pdf"
            >
              <DownloadSimple size={15} weight="bold" />
              {exporting ? "Generazione…" : "Scarica PDF"}
            </button>
            {cleanPhone && (
              <a
                href={`https://wa.me/${cleanPhone.replace(/^\+/, "")}?text=${waText}`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-2.5 rounded-md bg-[#25D366] hover:bg-[#1FBC58] text-white text-[12.5px] font-semibold transition-colors"
                data-testid="wa-send"
                title="Apri WhatsApp"
              >
                <WhatsappLogo size={15} weight="fill" />
              </a>
            )}
          </div>
        </div>

        {/* Conclusione + prezzo di partenza editabili */}
        <div className="mb-5 bg-white border border-[#EAE7DF] rounded-xl p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className="block text-[11px] uppercase tracking-[0.16em] text-[#6B6A65] font-semibold mb-1.5">
              La tua conclusione (apparirà sull'ultima pagina del PDF)
            </label>
            <textarea
              value={conclusion}
              onChange={(e) => setConclusion(e.target.value)}
              rows={3}
              maxLength={600}
              placeholder="Es. In base ai dati OMI e ai recenti rogiti nella tua zona, il valore di mercato realistico per la tua casa è tra €X e €Y."
              className="w-full px-3 py-2 text-[13.5px] border border-[#EAE7DF] rounded-md focus:outline-none focus:border-[#7C8B6E] bg-white"
              data-testid="conclusion-textarea"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.16em] text-[#6B6A65] font-semibold mb-1.5">
              Prezzo di partenza consigliato (€)
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={recommendedPrice}
              onChange={(e) => {
                const v = e.target.value.replace(/[^\d]/g, "").slice(0, 9);
                setRecommendedPrice(v ? Number(v).toLocaleString("it-IT") : "");
              }}
              placeholder="es. 520.000"
              className="w-full px-3 py-2 text-[15px] tabular-nums border border-[#EAE7DF] rounded-md focus:outline-none focus:border-[#7C8B6E] bg-white font-editorial"
              data-testid="recommended-price-input"
            />
            <p className="mt-1.5 text-[10.5px] text-[#9A968B] italic leading-snug">
              Opzionale. Se valorizzato, apparirà in evidenza sull'ultima pagina come "Prezzo di partenza consigliato".
            </p>
          </div>
        </div>

        {/* Preview PDF — 4 pagine A4 */}
        <div ref={previewRef} className="space-y-6">
          <PageCover lead={lead} agent={agent} input={input} result={result} brand={brand} />
          <PageOmiAndTrend
            input={input} result={result} city={city} omi={omi_snapshot}
            trend={trend_8q} trendMax={trendMax} trendMin={trendMin}
            timeToSell={time_to_sell} brand={brand}
          />
          <PageComparables comparables={comparables} city={city} agent={agent} brand={brand} />
          <PageContactAndConclusion agent={agent} conclusion={conclusion} recommendedPrice={recommendedPrice} brand={brand} />
        </div>
      </div>
    </div>
  );
}

// ─── A4 page wrapper ────────────────────────────────────────────────
function A4({ children, label }) {
  return (
    <div className="bg-white border border-[#EAE7DF] rounded-lg overflow-hidden shadow-sm">
      <div className="px-4 py-2 bg-[#FAFAF7] border-b border-[#EAE7DF] text-[10.5px] uppercase tracking-[0.18em] text-[#6B6A65] font-semibold">
        {label}
      </div>
      <div
        data-pdf-page
        className="bg-white"
        style={{ aspectRatio: "210/297", width: "100%" }}
      >
        {children}
      </div>
    </div>
  );
}

function PageCover({ lead, agent, input, result, brand }) {
  const fmt = (n) => Number(n || 0).toLocaleString("it-IT");
  // Widen range ±5% sul cliente (apertura negoziale)
  const widenedMin = Math.round((result?.range_min || 0) * 0.95);
  const widenedMax = Math.round((result?.range_max || 0) * 1.05);
  const logo = agent?.agency_logo_url;
  return (
    <A4 label="01 · Copertina">
      <div className="h-full p-10 flex flex-col" style={{ background: "linear-gradient(180deg, #FAFAF7 0%, #F1F4ED 100%)" }}>
        {/* Header: logo + nome agenzia */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {logo ? (
              <img src={logo} alt="" className="h-10 w-auto object-contain" crossOrigin="anonymous" />
            ) : (
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${brand}22` }}>
                <House size={20} weight="duotone" style={{ color: brand }} />
              </div>
            )}
            <div>
              <div className="font-editorial text-[15px] text-[#1C1C1A] leading-none">
                {agent?.agency_name || agent?.name || "Agenzia"}
              </div>
              {agent?.agency_name && agent?.name && (
                <div className="text-[10px] text-[#6B6A65] mt-0.5">a cura di {agent.name}</div>
              )}
            </div>
          </div>
          <div className="text-[9px] uppercase tracking-[0.22em] font-semibold" style={{ color: brand }}>
            Riservato · {lead?.name || ""}
          </div>
        </div>

        <div className="flex-1 flex flex-col justify-center my-8">
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#6B6A65]">
            Stima Comparativa di Mercato
          </div>
          <div className="text-[#1C1C1A] font-editorial text-[44px] leading-[1.04] tracking-tight mt-2">
            {input?.city || "Immobile"}
            {input?.address ? (
              <>
                <br/>
                <span className="italic text-[30px]" style={{ color: brand }}>{input.address}</span>
              </>
            ) : null}
          </div>
          <div className="mt-7 grid grid-cols-3 gap-4 text-[12px]">
            <Stat label="Superficie" value={`${input?.surface_sqm || "—"} m²`} brand={brand} />
            <Stat label="Locali" value={input?.rooms || "—"} brand={brand} />
            <Stat label="Stato" value={prettyState(input?.state)} brand={brand} />
          </div>
          <div className="mt-8 pt-6 border-t border-[#1C1C1A]/10">
            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#6B6A65]">
              Range di mercato indicativo
            </div>
            <div className="font-editorial text-[44px] text-[#1C1C1A] tabular-nums mt-1 leading-none">
              €{fmt(widenedMin)} – €{fmt(widenedMax)}
            </div>
            <div className="text-[11px] text-[#6B6A65] italic mt-1">
              Basato su dati OMI Agenzia delle Entrate · valore preciso da definire dopo sopralluogo
            </div>
          </div>
        </div>

        <div className="flex items-end justify-between">
          <div className="text-[10px] text-[#9A968B]">
            Documento generato per
            <div className="text-[#1C1C1A] font-semibold text-[12px] mt-0.5">{lead?.name || ""}</div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-[#9A968B]">
            {new Date().toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}
          </div>
        </div>
      </div>
    </A4>
  );
}

function PageOmiAndTrend({ input, result, city, omi, trend, trendMax, trendMin, timeToSell, brand }) {
  const fmt = (n) => Number(n || 0).toLocaleString("it-IT");
  // Stretch del grafico: ymin = trendMin × 0.94 per esagerare visivamente la curva
  const yFloor = Math.floor(trendMin * 0.94);
  const yRange = Math.max(1, trendMax - yFloor);
  // YoY: delta tra Q1/2026 e Q1/2025 (indici 0..7, last = 7, q1_2025 = 3)
  let yoy = null;
  if (trend && trend.length === 8) {
    const now = trend[7].value;
    const yearAgo = trend[3].value;
    if (yearAgo > 0) yoy = ((now - yearAgo) / yearAgo) * 100;
  }
  return (
    <A4 label="02 · Mercato OMI + trend 8 trimestri">
      <div className="h-full p-10 flex flex-col">
        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold" style={{ color: brand }}>
          Mercato OMI · {city || "—"}
        </div>
        <h2 className="font-editorial text-[28px] text-[#1C1C1A] mt-2 leading-tight">
          Valori ufficiali aggiornati
        </h2>
        <p className="mt-3 text-[12px] text-[#3F3D38] leading-relaxed">
          L'Osservatorio del Mercato Immobiliare dell'Agenzia delle Entrate pubblica trimestralmente i prezzi medi delle compravendite per ogni microzona italiana. Questi sono i valori per la zona del tuo immobile, aggiustati per stato e caratteristiche.
        </p>

        <div className="mt-5 grid grid-cols-3 gap-3">
          <BigStat label="Prezzo medio /m²" value={`€${fmt(result?.price_per_sqm || omi?.median_per_sqm)}`} sub="zona OMI di riferimento" brand={brand} />
          <BigStat label="Minimo /m²" value={`€${fmt(result?.price_per_sqm_min || omi?.min_per_sqm)}`} sub="immobili da rinfrescare" brand={brand} />
          <BigStat label="Massimo /m²" value={`€${fmt(result?.price_per_sqm_max || omi?.max_per_sqm)}`} sub="ottimo stato, piano alto" brand={brand} />
        </div>

        {/* Trend grafico — area + barre */}
        <div className="mt-5 flex-1 flex flex-col">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#6B6A65]">
              Andamento €/m² ultimi 8 trimestri
            </div>
            {yoy !== null && (
              <div className="text-[11px] font-semibold" style={{ color: yoy >= 0 ? "#3F7D4A" : "#B14438" }}>
                {yoy >= 0 ? "+" : ""}{yoy.toFixed(1)}% YoY
              </div>
            )}
          </div>
          <div className="flex items-end gap-2 h-36 border-b border-[#EAE7DF] pb-1 px-1">
            {(trend || []).map((t, i) => {
              const h = ((t.value - yFloor) / yRange) * 100;
              return (
                <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
                  <div className="text-[8.5px] tabular-nums text-[#6B6A65] mb-1">€{(t.value / 1000).toFixed(1)}k</div>
                  <div
                    className="w-full rounded-t-sm"
                    style={{
                      height: `${Math.max(8, h)}%`,
                      background: i === (trend.length - 1)
                        ? `linear-gradient(180deg, ${brand} 0%, ${brand}cc 100%)`
                        : `linear-gradient(180deg, ${brand}99 0%, ${brand}66 100%)`,
                    }}
                  />
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-2 mt-1.5 px-1">
            {(trend || []).map((t, i) => (
              <div key={i} className="flex-1 text-center text-[8.5px] text-[#6B6A65]">{t.label}</div>
            ))}
          </div>
          <p className="mt-3 text-[10.5px] text-[#6B6A65] italic leading-snug">
            Fonte: serie €/m² OMI · indice ISTAT-Banca d'Italia prezzi abitazioni Italia (residenziale).
          </p>
        </div>

        <div className="mt-auto pt-5 border-t border-[#EAE7DF]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${brand}1f` }}>
              <Timer size={20} weight="duotone" style={{ color: brand }} />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#6B6A65]">Tempo medio di vendita in zona</div>
              <div className="font-editorial text-[22px] text-[#1C1C1A] mt-0.5 leading-tight">
                {timeToSell?.min_months || 5} – {timeToSell?.max_months || 8} mesi
                <span className="text-[12px] text-[#6B6A65] font-normal ml-2">se prezzato correttamente</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </A4>
  );
}

function PageComparables({ comparables, city, agent, brand }) {
  const list = comparables || [];
  const soldCount = list.filter((c) => c.kind === "sold").length;
  return (
    <A4 label="03 · Immobili comparabili">
      <div className="h-full p-10 flex flex-col">
        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold" style={{ color: brand }}>
          La nostra esperienza concreta
        </div>
        <h2 className="font-editorial text-[28px] text-[#1C1C1A] mt-2 leading-tight">
          {soldCount > 0 ? "Cosa abbiamo venduto in zona" : "Immobili come il tuo che gestiamo"}
        </h2>
        <p className="mt-3 text-[12px] text-[#3F3D38] leading-relaxed">
          {list.length > 0
            ? (soldCount > 0
                ? `Ecco alcuni immobili realmente venduti dalla nostra agenzia ${city ? `a ${city}` : ""}. Questi sono prezzi di rogito, non richieste.`
                : `Per dare un riferimento concreto, ecco alcuni immobili simili al tuo che gestiamo direttamente ${city ? `a ${city}` : ""}.`)
            : `Per ora la valutazione si basa su dati OMI ufficiali. Per una stima ancora più precisa, possiamo organizzare un sopralluogo: in 20 minuti capiremo prospettive, esposizione e potenziale.`}
        </p>

        <div className="mt-5 space-y-3 flex-1 overflow-hidden">
          {list.length === 0 && (
            <div className="bg-[#F1F4ED] border border-[#7C8B6E]/20 rounded-lg p-5 text-[12px] text-[#3F3D38] italic leading-relaxed">
              La nostra agenzia non ha ancora compravendite registrate{city ? ` a ${city}` : ""}. La stima si basa esclusivamente sui dati OMI ufficiali e sull'esperienza maturata in ambito immobiliare locale.
            </div>
          )}
          {list.map((p, idx) => (
            <ComparableRow key={`${p.kind}-${p.id || idx}`} c={p} brand={brand} />
          ))}
        </div>

        {agent && (agent.active_count > 0 || agent.sold_count > 0) && (
          <div className="mt-4 grid grid-cols-2 gap-3 pt-4 border-t border-[#EAE7DF]">
            <MiniMetric label="Immobili in portafoglio" value={agent.active_count || 0} brand={brand} />
            <MiniMetric label="Rogiti chiusi finora" value={agent.sold_count || 0} brand={brand} />
          </div>
        )}

        <p className="mt-3 text-[10px] text-[#9A968B] italic leading-snug">
          Dati estratti direttamente dal CRM dell'agenzia. Per dettagli su ciascun caso, chiedi all'agente di riferimento.
        </p>
      </div>
    </A4>
  );
}

function ComparableRow({ c, brand }) {
  const fmt = (n) => Number(n || 0).toLocaleString("it-IT");
  const isSold = c.kind === "sold";
  const price = c.final_price_eur || c.asking_price_eur;
  return (
    <div className="flex items-start gap-3 bg-white border border-[#EAE7DF] rounded-lg p-3">
      <div
        className="w-14 h-14 rounded-md flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: isSold ? `${brand}1f` : "#F1F4ED" }}
      >
        {isSold ? (
          <CheckCircle size={22} weight="fill" style={{ color: brand }} />
        ) : (
          <Buildings size={22} weight="duotone" style={{ color: brand }} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <div className="font-editorial text-[14.5px] text-[#1C1C1A] truncate">{c.title}</div>
          {isSold && (
            <span className="text-[9px] uppercase tracking-[0.16em] font-semibold px-1.5 py-0.5 rounded" style={{ color: brand, backgroundColor: `${brand}1f` }}>
              Venduto
            </span>
          )}
        </div>
        <div className="text-[11px] text-[#6B6A65] mt-0.5">
          {c.city || "—"}{c.address ? ` · ${c.address}` : ""}
        </div>
        <div className="text-[11px] text-[#3F3D38] mt-0.5">
          {c.surface_sqm ? `${c.surface_sqm} m²` : ""}
          {c.rooms ? `${c.surface_sqm ? " · " : ""}${c.rooms} locali` : ""}
          {c.state ? ` · ${prettyState(c.state)}` : ""}
          {isSold && c.days_on_market != null ? `${(c.surface_sqm || c.rooms || c.state) ? " · " : ""}venduto in ${c.days_on_market} gg` : ""}
        </div>
      </div>
      {price ? (
        <div className="text-right flex-shrink-0">
          <div className="font-editorial text-[15px] text-[#1C1C1A] tabular-nums leading-tight">
            €{fmt(price)}
          </div>
          <div className="text-[9px] uppercase tracking-[0.14em] text-[#6B6A65] mt-0.5">
            {isSold ? "Rogito" : "Richiesta"}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function PageContactAndConclusion({ agent, conclusion, recommendedPrice, brand }) {
  return (
    <A4 label="04 · Conclusione e contatti">
      <div className="h-full p-10 flex flex-col">
        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold" style={{ color: brand }}>
          Il prossimo passo
        </div>
        <h2 className="font-editorial text-[28px] text-[#1C1C1A] mt-2 leading-tight">
          Conclusione e raccomandazione
        </h2>

        <div className="mt-6 bg-[#FAFAF7] border-l-4 p-5 rounded-r-lg" style={{ borderColor: brand }}>
          <p className="text-[13px] text-[#1C1C1A] leading-relaxed italic">
            {conclusion || "Il valore di mercato indicativo della tua casa si colloca nel range mostrato a pag. 1. Una volta effettuato il sopralluogo, potrò darti una valutazione più precisa e un piano di vendita realistico. Sono a tua disposizione per il prossimo passo."}
          </p>
        </div>

        {recommendedPrice && (
          <div className="mt-4 rounded-lg p-5 flex items-center justify-between gap-4" style={{ backgroundColor: `${brand}12`, border: `1.5px solid ${brand}66` }}>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: brand }}>
                Prezzo di partenza consigliato
              </div>
              <p className="text-[11px] text-[#3F3D38] mt-1 leading-snug italic">
                Valore strategico per attivare l'interesse del mercato nelle prime 2-3 settimane.
              </p>
            </div>
            <div className="font-editorial leading-none tabular-nums whitespace-nowrap" style={{ color: brand, fontSize: "36px" }}>
              €{recommendedPrice}
            </div>
          </div>
        )}

        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#6B6A65] mb-2">
            Perché affidarti a noi
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Pill icon={<CheckCircle size={14} weight="fill" />} text="Solo prezzi reali, mai gonfiati" brand={brand} />
            <Pill icon={<MapPin size={14} weight="fill" />} text="Specialisti della tua zona" brand={brand} />
            <Pill icon={<Timer size={14} weight="fill" />} text="Sopralluogo entro 48 ore" brand={brand} />
          </div>
        </div>

        <div className="mt-auto pt-8 border-t border-[#EAE7DF]">
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#6B6A65] mb-3">Il tuo agente di riferimento</div>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden" style={{ backgroundColor: `${brand}1f` }}>
              {agent?.picture ? (
                <img src={agent.picture} alt="" className="w-full h-full object-cover" crossOrigin="anonymous" />
              ) : (
                <House size={24} weight="duotone" style={{ color: brand }} />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-editorial text-[20px] text-[#1C1C1A] leading-tight">{agent?.name || "Agente"}</div>
              <div className="text-[12px] text-[#6B6A65]">{agent?.agency_name || ""}</div>
              <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-[#3F3D38]">
                {agent?.phone && (
                  <span className="inline-flex items-center gap-1"><Phone size={11} weight="fill" style={{ color: brand }} /> {agent.phone}</span>
                )}
                {agent?.agency_phone && agent.agency_phone !== agent.phone && (
                  <span className="inline-flex items-center gap-1"><Phone size={11} weight="bold" style={{ color: brand }} /> {agent.agency_phone}</span>
                )}
                {agent?.email && (
                  <span className="inline-flex items-center gap-1"><EnvelopeSimple size={11} weight="fill" style={{ color: brand }} /> {agent.email}</span>
                )}
              </div>
              {agent?.agency_address && (
                <div className="mt-0.5 inline-flex items-center gap-1 text-[10.5px] text-[#6B6A65]">
                  <MapPin size={10} weight="fill" style={{ color: brand }} /> {agent.agency_address}
                </div>
              )}
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-2 text-[10px] text-[#6B6A65]">
            {agent?.agency_rea && (
              <div className="inline-flex items-center gap-1.5">
                <IdentificationCard size={11} weight="duotone" style={{ color: brand }} />
                <span>REA: <span className="text-[#1C1C1A] font-semibold tabular-nums">{agent.agency_rea}</span></span>
              </div>
            )}
            <div className="text-right">
              Documento generato il {new Date().toLocaleDateString("it-IT")}
            </div>
          </div>

          <p className="mt-3 text-[9.5px] text-[#9A968B] italic leading-snug">
            Documento informativo a uso del cliente · stima indicativa basata su dati OMI Agenzia delle Entrate e serie storica ISTAT, non costituisce perizia tecnica giurata né proposta contrattuale.
          </p>
        </div>
      </div>
    </A4>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────
function Stat({ label, value, brand }) {
  return (
    <div className="border-l-2 pl-2" style={{ borderColor: brand }}>
      <div className="text-[9px] uppercase tracking-[0.16em] font-semibold text-[#6B6A65]">{label}</div>
      <div className="font-editorial text-[15px] text-[#1C1C1A] mt-0.5 leading-tight">{value}</div>
    </div>
  );
}

function BigStat({ label, value, sub, brand }) {
  return (
    <div className="bg-[#FAFAF7] border border-[#EAE7DF] rounded-lg p-3" style={{ borderTopColor: brand, borderTopWidth: 2 }}>
      <div className="text-[9.5px] uppercase tracking-[0.16em] font-semibold text-[#6B6A65]">{label}</div>
      <div className="font-editorial text-[22px] text-[#1C1C1A] mt-0.5 leading-tight tabular-nums">{value}</div>
      <div className="text-[10px] text-[#9A968B] mt-0.5 italic">{sub}</div>
    </div>
  );
}

function MiniMetric({ label, value, brand }) {
  return (
    <div className="flex items-baseline gap-2">
      <div className="font-editorial text-[28px] leading-none tabular-nums" style={{ color: brand }}>{value}</div>
      <div className="text-[10.5px] text-[#6B6A65] leading-tight">{label}</div>
    </div>
  );
}

function Pill({ icon, text, brand }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-white border border-[#EAE7DF]">
      <span style={{ color: brand }}>{icon}</span>
      <span className="text-[10.5px] text-[#3F3D38] leading-tight">{text}</span>
    </div>
  );
}

function prettyState(s) {
  return {
    ottimo: "Ottimo",
    buono: "Buono",
    da_ristrutturare: "Da ristrutturare",
    nuovo: "Nuovo",
  }[s] || s || "—";
}
